import type { TimeReportGroupByLevel } from '@hygge/shared';
import type { ReportGroupLevel } from './report-builder-state';

/**
 * The three report shapes accounting reads day to day, as one-click presets on
 * the Reports screen. [HW-877]
 *
 * Each is nothing more than a grouping the Builder could already express; what
 * a preset adds is a name and a single click where there used to be a control
 * to understand. The order of the levels is the hierarchy — the first is the
 * top-level row, the second nests beneath it — which is the same rule the
 * Group By picker follows for the order of its clicks.
 *
 * Zero-hour people and projects are absent from all three by construction: the
 * server aggregates entries, and a person with no entries has no row to
 * aggregate. The PM confirmed that is the wanted behaviour, not a gap.
 */

/** One of the three shapes, by id. */
export type ReportPresetId = 'person' | 'person-projects' | 'project-people';

export interface ReportPreset {
  id: ReportPresetId;
  label: string;
  /** What the table will show, in one sentence, for the button's tooltip. */
  description: string;
  groupBy: readonly [ReportGroupLevel] | readonly [ReportGroupLevel, ReportGroupLevel];
}

export const REPORT_PRESETS: readonly ReportPreset[] = [
  {
    id: 'person',
    label: 'By Person',
    description: 'Each person’s total hours for the period, nothing underneath.',
    groupBy: ['employee'],
  },
  {
    id: 'person-projects',
    label: 'By Person, per project',
    description: 'Each person’s total, broken down by the projects they logged to.',
    groupBy: ['employee', 'project'],
  },
  {
    id: 'project-people',
    label: 'By Project',
    description: 'Each project’s total, with everyone who logged to it and their hours.',
    groupBy: ['project', 'employee'],
  },
];

/**
 * Which preset a grouping IS, if any — so the button for the shape on screen
 * can read as pressed, whether it got there by the button or by the picker.
 *
 * Order-sensitive on purpose: `['employee','project']` and `['project',
 * 'employee']` are two different presets, because the first level is the
 * top-level row.
 */
export function presetOf(groupBy: readonly TimeReportGroupByLevel[]): ReportPresetId | null {
  const match = REPORT_PRESETS.find(
    (preset) =>
      preset.groupBy.length === groupBy.length &&
      preset.groupBy.every((level, index) => level === groupBy[index])
  );
  return match?.id ?? null;
}

/**
 * Which preset button should read as pressed.
 *
 * A preset is a summary by definition, so none is pressed while the screen
 * shows the line-per-entry view — even though the grouping underneath may be
 * exactly a preset's. Without the second argument the By Person button stayed
 * lit above a raw log list, one click from the screen's opening state.
 */
export function activePresetOf(
  groupBy: readonly TimeReportGroupByLevel[],
  entriesMode: boolean
): ReportPresetId | null {
  return entriesMode ? null : presetOf(groupBy);
}
